import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

// Hand-curated graph of topics + edges (uses /api/knowledge items + computed positions)
const POSITIONS = {
  k_ai_economy: { x: 480, y: 120 },
  k_glass_design: { x: 720, y: 280 },
  k_neg_ai: { x: 200, y: 280 },
  k_living_feeds: { x: 480, y: 440 },
};
const EDGES = [
  ["k_ai_economy", "k_living_feeds"],
  ["k_ai_economy", "k_neg_ai"],
  ["k_glass_design", "k_living_feeds"],
  ["k_glass_design", "k_ai_economy"],
  ["k_neg_ai", "k_living_feeds"],
];

const COLORS = {
  creator: "#00E5FF",
  design: "#7B2CBF",
  business: "#F5C542",
  social: "#FF6B9D",
};

export default function KnowledgeGraph({ items, selected, onSelect }) {
  const [hover, setHover] = useState(null);
  const [dims, setDims] = useState({ w: 900, h: 560 });
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setDims({ w, h: Math.max(420, Math.min(560, w * 0.6)) });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const scale = dims.w / 900;
  const px = (p) => ({ x: p.x * scale, y: p.y * (dims.h / 560) });

  return (
    <div ref={ref} className="relative w-full">
      <svg width={dims.w} height={dims.h} className="overflow-visible" data-testid="knowledge-graph">
        {/* Background grid */}
        <defs>
          <radialGradient id="bg-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7B2CBF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#0A0B1A" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="edge-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#7B2CBF" stopOpacity="0.6" />
          </linearGradient>
          <filter id="node-glow">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <rect width={dims.w} height={dims.h} fill="url(#bg-glow)" />

        {/* Edges */}
        {EDGES.map(([a, b]) => {
          const pa = POSITIONS[a]; const pb = POSITIONS[b];
          if (!pa || !pb) return null;
          const A = px(pa); const B = px(pb);
          const active = hover === a || hover === b || selected === items.find((it) => it.id === a)?.title || selected === items.find((it) => it.id === b)?.title;
          return (
            <line
              key={`edge-${a}-${b}`}
              x1={A.x} y1={A.y} x2={B.x} y2={B.y}
              stroke="url(#edge-grad)"
              strokeWidth={active ? 2.5 : 1.4}
              strokeOpacity={active ? 1 : 0.5}
              strokeDasharray={active ? "0" : "4 4"}
            />
          );
        })}

        {/* Nodes */}
        {items.map((it) => {
          const p = POSITIONS[it.id];
          if (!p) return null;
          const { x, y } = px(p);
          const color = COLORS[it.topic] || "#00E5FF";
          const isSelected = selected === it.title;
          const isHover = hover === it.id;
          return (
            <motion.g
              key={it.id}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              onMouseEnter={() => setHover(it.id)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect(it.title)}
              style={{ cursor: "pointer" }}
              data-testid={`graph-node-${it.id}`}
            >
              <circle
                cx={x} cy={y}
                r={isSelected ? 36 : isHover ? 30 : 24}
                fill={`${color}22`}
                stroke={color}
                strokeWidth={isSelected ? 2.5 : 1.5}
                filter={isSelected || isHover ? "url(#node-glow)" : ""}
              />
              <circle cx={x} cy={y} r={isSelected ? 10 : 7} fill={color} />
              <text
                x={x}
                y={y + (isSelected ? 54 : 46)}
                textAnchor="middle"
                fill="#0F172A"
                style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 600, fontSize: 12 }}
              >
                {it.title}
              </text>
              <text
                x={x}
                y={y + (isSelected ? 70 : 62)}
                textAnchor="middle"
                fill="#A0A5C0"
                style={{ fontFamily: "Chivo, sans-serif", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase" }}
              >
                {it.topic}
              </text>
            </motion.g>
          );
        })}
      </svg>
    </div>
  );
}
